
import { Component, Input, Output, EventEmitter, forwardRef, ElementRef, HostListener, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface DatePickerConfig {
  label: string;
  value?: string | Date;
  placeholder?: string;
  id: string;
  name: string;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  format?: 'date' | 'datetime-local' | 'month' | 'week';
}

@Component({
  selector: 'app-date-picker',
  standalone: false,
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true
    }
  ]
})
export class DatePickerComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() config!: DatePickerConfig;
  @Input() label?: string;
  @Input() value: string | Date = '';
  @Input() placeholder: string = 'mm/dd/yyyy';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() min?: string;
  @Input() max?: string;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() format: 'date' | 'datetime-local' | 'month' | 'week' = 'date';
  @Input() inline: boolean = false;
  @Input() isLabelStandard: boolean = false;


  showDropdown = false;
  viewMode: 'days' | 'months' | 'years' = 'days';
  viewDate: Date = new Date();
  days: { date: Date; isCurrentMonth: boolean; isSelected: boolean; isToday: boolean; isDisabled: boolean }[] = [];
  weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  years: number[] = [];
  isFocused: boolean = false;
  isTouched: boolean = false;
  calendarStyle: any = {};
  private isTyping: boolean = false;
  private currentInputValue: string = '';
  
  // Constants
  private readonly MAX_DIGITS = 8;
  private readonly DATE_FORMAT_LENGTH = 10;
  private readonly MIN_YEAR = 1900;
  private readonly MAX_YEAR = 2100;
  private readonly CALENDAR_HEIGHT = 350;
  private readonly DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  private readonly ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;
  
  // Cached today's date (reset at midnight)
  private cachedToday: Date | null = null;
  private cachedTodayDate: number = 0;
  private _captureClickHandler = (event: Event) => this.onCaptureClick(event);

  constructor(private elementRef: ElementRef) {
    this.generateCalendar();
    this.generateYears();
  }

  ngOnInit(): void {
    if (this.inline) {
      this.showDropdown = true;
      this.viewMode = 'days';
      this.initViewDate();
      this.generateCalendar();
      this.generateYears();
    } else {
      // capture-phase click listener so we can detect outside clicks even if bubble is stopped.
      // Inline calendars do not use popup outside-close; skip to avoid global listener churn.
      document.addEventListener('click', this._captureClickHandler, true);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.generateCalendar();
    }
    // Regenerate calendar when min or max changes to update disabled dates
    if (changes['min'] || changes['max'] || changes['config']) {
      if (this.showDropdown || this.inline) {
        this.generateCalendar();
      }
    }
  }

  /**
   * Outside-close is handled by capture-phase click only (see onCaptureClick).
   * A bubble-phase document:click duplicated work and ran contains() on every click app-wide.
   */

  @HostListener('document:focusin', ['$event'])
  @HostListener('document:touchend', ['$event'])
  onInteractionOutside(event: Event): void {
    if (this.inline || !this.showDropdown) return;
    const target = (event && (event as any).target) as Node | null;
    if (!target) return;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.showDropdown = false;
    }
  }

  ngOnDestroy(): void {
    if (!this.inline) {
      document.removeEventListener('click', this._captureClickHandler, true);
    }
  }

  private onCaptureClick(event: Event): void {
    if (this.inline || !this.showDropdown) return;
    const target = (event && (event as any).target) as Node | null;
    if (!target) return;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.showDropdown = false;
    }
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onScrollOrResize(): void {
    if (this.showDropdown && !this.inline) {
      this.calculateCalendarPosition();
    }
  }

  toggleDropdown(): void {
    if (this.inline) return;
    if (!this.isDisabled && !this.isReadonly) {
      this.showDropdown = !this.showDropdown;
      if (this.showDropdown) {
        this.viewMode = 'days';
        this.initViewDate();
        this.generateCalendar();
        this.generateYears();
        this.calculateCalendarPosition();
      }
    }
  }

  private calculateCalendarPosition(): void {
    const inputElement = this.elementRef.nativeElement.querySelector('input');
    if (!inputElement) return;

    const rect = inputElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const baseStyle = {
      position: 'fixed' as const,
      left: `${rect.left}px`,
      width: '240px',
      'z-index': '99999'
    };

    // Calculate fixed position to break out of parent containers
    if (spaceBelow < this.CALENDAR_HEIGHT && rect.top > spaceBelow) {
      // Open above if there's not enough space below
      this.calendarStyle = {
        ...baseStyle,
        bottom: `${viewportHeight - rect.top + 6}px`
      };
    } else {
      // Open below
      this.calendarStyle = {
        ...baseStyle,
        top: `${rect.bottom + 6}px`
      };
    }
  }

  private initViewDate(): void {
    if (this.value) {
      const d = typeof this.value === 'string' ? this.parseValueToDate(this.value) : this.value;
      if (d && !isNaN(d.getTime())) {
        this.viewDate = new Date(d);
        return;
      }
    }
    this.viewDate = new Date();
  }
  
  private parseValueToDate(value: string): Date | null {
    // Try parsing YYYY-MM-DD format first
    const isoMatch = value.match(this.ISO_DATE_PATTERN);
    if (isoMatch) {
      return new Date(parseInt(isoMatch[1], 10), parseInt(isoMatch[2], 10) - 1, parseInt(isoMatch[3], 10));
    }
    // Try parsing as regular date string
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }
  
  private normalizeDate(date: Date): Date {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }
  
  private getToday(): Date {
    const now = new Date();
    const todayDate = now.getDate();
    // Cache today's date, but reset if date changed
    if (!this.cachedToday || this.cachedTodayDate !== todayDate) {
      this.cachedToday = this.normalizeDate(now);
      this.cachedTodayDate = todayDate;
    }
    return this.cachedToday;
  }

  generateYears(): void {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 100;
    this.years = Array.from({ length: 201 }, (_, i) => startYear + i);
  }

  showMonths(event: MouseEvent): void {
    event.stopPropagation();
    this.viewMode = this.viewMode === 'months' ? 'days' : 'months';
  }

  showYears(event: MouseEvent): void {
    event.stopPropagation();
    this.viewMode = this.viewMode === 'years' ? 'days' : 'years';
    if (this.viewMode === 'years') {
      this.scrollToActiveYear();
    }
  }

  private scrollToActiveYear(): void {
    setTimeout(() => {
      const activeYear = this.viewDate.getFullYear();
      const element = document.getElementById(`year-${activeYear}`);
      if (element) {
        element.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }, 100);
  }

  selectMonth(month: number, event: MouseEvent): void {
    event.stopPropagation();
    this.viewDate = new Date(this.viewDate.getFullYear(), month, 1);
    this.generateCalendar();
    this.viewMode = 'days';
  }

  selectYear(year: number, event: MouseEvent): void {
    event.stopPropagation();
    this.viewDate = new Date(year, this.viewDate.getMonth(), 1);
    this.generateCalendar();
    this.generateYears();
    this.viewMode = 'days';
  }

  onPrevClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.viewMode === 'days') {
      this.prevMonth();
    } else if (this.viewMode === 'months') {
      this.viewDate = new Date(this.viewDate.getFullYear() - 1, this.viewDate.getMonth(), 1);
    } else if (this.viewMode === 'years') {
      const currentStart = this.years[0];
      this.years = Array.from({ length: 201 }, (_, i) => currentStart - 50 + i);
    }
  }

  onNextClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.viewMode === 'days') {
      // Allow navigating to the next month even when the 1st is outside min/max; individual days stay disabled.
      this.nextMonth();
    } else if (this.viewMode === 'months') {
      this.viewDate = new Date(this.viewDate.getFullYear() + 1, this.viewDate.getMonth(), 1);
    } else if (this.viewMode === 'years') {
      const currentStart = this.years[0];
      this.years = Array.from({ length: 201 }, (_, i) => currentStart + 50 + i);
    }
  }
  
  private isDateDisabled(date: Date): boolean {
    const normalizedDate = this.normalizeDate(date);
    
    // Check min date constraint - allow same date (>=)
    if (this.displayMin) {
      const minDate = this.normalizeDate(new Date(this.displayMin));
      // Normalize both dates to compare only date part (ignore time)
      const dateOnly = new Date(normalizedDate.getFullYear(), normalizedDate.getMonth(), normalizedDate.getDate());
      const minDateOnly = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
      
      // Disable if date is before min date (same date is allowed)
      if (dateOnly.getTime() < minDateOnly.getTime()) {
        return true;
      }
    }
    
    // Check max date constraint
    if (this.displayMax) {
      const maxDate = this.normalizeDate(new Date(this.displayMax));
      maxDate.setHours(23, 59, 59, 999);
      // Normalize both dates to compare only date part (ignore time)
      const dateOnly = new Date(normalizedDate.getFullYear(), normalizedDate.getMonth(), normalizedDate.getDate());
      const maxDateOnly = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
      
      // Disable if date is after max date
      if (dateOnly.getTime() > maxDateOnly.getTime()) {
        return true;
      }
    }
    
    return false;
  }

  prevYear(event: MouseEvent): void {
    event.stopPropagation();
    const currentStart = this.years[0];
    this.years = Array.from({ length: 201 }, (_, i) => currentStart - 21 + i);
  }

  nextYear(event: MouseEvent): void {
    event.stopPropagation();
    const currentEnd = this.years[this.years.length - 1];
    this.years = Array.from({ length: 201 }, (_, i) => currentEnd + 1 + i);
  }

  generateCalendar(): void {
    const year = this.viewDate.getFullYear();
    const month = this.viewDate.getMonth();

    // First day of month
    const firstDay = new Date(year, month, 1);
    // Last day of month
    const lastDay = new Date(year, month + 1, 0);

    // Day of week for first day (0-6)
    const firstDayOfWeek = firstDay.getDay();

    const days = [];

    // Previous month's trailing days
    const prevMonthLastDay = new Date(year, month, 0);
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevMonthLastDay.getDate() - i);
      days.push(this.createDay(d, false));
    }

    // Current month's days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const d = new Date(year, month, i);
      days.push(this.createDay(d, true));
    }

    // Next month's leading days
    const totalDaysRemaining = 42 - days.length; // 6 rows of 7 days
    for (let i = 1; i <= totalDaysRemaining; i++) {
      const d = new Date(year, month + 1, i);
      days.push(this.createDay(d, false));
    }

    this.days = days;
  }

  private createDay(date: Date, isCurrentMonth: boolean) {
    const normalizedDate = this.normalizeDate(date);
    const today = this.getToday();
    
    const isSelected = this.isDateSelected(normalizedDate);
    const isDisabled = this.isDateDisabled(normalizedDate);

    return {
      date,
      isCurrentMonth,
      isSelected,
      isToday: normalizedDate.getTime() === today.getTime(),
      isDisabled
    };
  }
  
  private isDateSelected(date: Date): boolean {
    if (!this.value) return false;
    
    let selectedDate: Date | null = null;
    if (this.value instanceof Date) {
      selectedDate = this.normalizeDate(this.value);
    } else if (typeof this.value === 'string') {
      selectedDate = this.parseValueToDate(this.value);
      if (selectedDate) {
        selectedDate = this.normalizeDate(selectedDate);
      }
    }
    
    return selectedDate ? date.getTime() === selectedDate.getTime() : false;
  }

  prevMonth(event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1);
    this.generateCalendar();
  }

  nextMonth(event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1);
    this.generateCalendar();
  }

  selectDate(date: Date, event: MouseEvent): void {
    event.stopPropagation();

    const normalizedDate = this.normalizeDate(date);
    if (this.isDateDisabled(normalizedDate)) {
      return;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const val = `${year}-${month}-${day}`;

    this.value = val;
    this.onChange(val);
    this.valueChange.emit(val);
    this.generateCalendar();
    if (!this.inline) {
      this.showDropdown = false;
    }
  }

  selectToday(event: MouseEvent): void {
    event.stopPropagation();
    const today = this.getToday();
    if (!this.isDateDisabled(today)) {
      this.selectDate(today, event);
    }
  }

  clearDate(event: MouseEvent): void {
    event.stopPropagation();
    this.value = '';
    this.onChange('');
    this.valueChange.emit('');
    this.generateCalendar();
    if (!this.inline) {
      this.showDropdown = false;
    }
  }

  @Output() valueChange = new EventEmitter<string>();
  @Output() blur = new EventEmitter<FocusEvent>();


  private onChange = (value: string) => { };
  private onTouched = () => { };

  onDateChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    let value = target.value;
    this.isTyping = true;
    this.currentInputValue = value;

    // Format input as user types (MM/dd/yyyy) - only format structure, don't modify year
    const formatted = this.formatDateInput(value);
    
    // Update the input field with formatted value (only if structure changed, not content)
    if (target.value !== formatted) {
      const cursorPos = target.selectionStart || 0;
      target.value = formatted;
      this.currentInputValue = formatted;
      // Restore cursor position after formatting
      const newCursorPos = Math.min(cursorPos, formatted.length);
      setTimeout(() => {
        target.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }

    // Store the current display value to preserve what user is typing
    // Don't update internal value until date is complete and validated
    if (!formatted) {
      // Empty value
      this.isTyping = false;
      this.currentInputValue = '';
      this.value = '';
      this.onChange('');
      this.valueChange.emit('');
      this.generateCalendar();
      return;
    }

    // Only parse and validate if the date is complete (MM/dd/yyyy = 10 characters)
    if (formatted.length === this.DATE_FORMAT_LENGTH) {
      const validationResult = this.validateAndParseDate(formatted);
      if (validationResult) {
        if (this.isDateDisabled(validationResult.date)) {
          target.value = this.currentInputValue || this.displayValue || '';
          this.isTyping = false;
          return;
        }
        
        // Valid date - preserve exact year as typed
        const parsedValue = `${validationResult.yearStr}-${validationResult.monthStr}-${validationResult.dayStr}`;
        this.isTyping = false;
        this.currentInputValue = formatted;
        this.value = parsedValue;
        this.onChange(parsedValue);
        this.valueChange.emit(parsedValue);
        this.generateCalendar();
      } else {
        this.isTyping = false;
      }
    }
    // Still typing - keep isTyping true to prevent displayValue from overwriting
  }

  onKeyDown(event: KeyboardEvent): void {
    const char = event.key;
    const target = event.target as HTMLInputElement;
    const currentValue = target.value || '';
    const cursorPos = target.selectionStart || 0;
    const selectionEnd = target.selectionEnd || 0;
    const hasSelection = cursorPos !== selectionEnd;

    if (char === 'Backspace') {
      if (hasSelection) return;
      if (cursorPos > 0) {
        event.preventDefault();
        this.handleBackspace(target, currentValue, cursorPos);
      }
      return;
    }

    if (char === 'Delete') {
      if (hasSelection) return;
      if (cursorPos < currentValue.length) {
        event.preventDefault();
        this.handleDelete(target, currentValue, cursorPos);
      }
      return;
    }

    // Allow: tab, arrow keys, etc.
    if (char === 'Tab' || 
        char === 'ArrowLeft' || char === 'ArrowRight' || char === 'ArrowUp' || 
        char === 'ArrowDown' || char === 'Home' || char === 'End' ||
        (event.ctrlKey && (char === 'a' || char === 'c' || char === 'v' || char === 'x'))) {
      return; // Allow these keys
    }

    // Only allow digits
    if (!/\d/.test(char)) {
      event.preventDefault();
      return;
    }

    // Get digits only from current value
    const digitsOnly = currentValue.replace(/\D/g, '');
    
    // Prevent typing if already at max length
    if (digitsOnly.length >= this.MAX_DIGITS) {
      event.preventDefault();
      return;
    }

    // Validate month as user types
    if (digitsOnly.length === 1) {
      const firstDigit = parseInt(digitsOnly[0] || '0', 10);
      if (firstDigit === 1 && parseInt(char, 10) > 2) {
        // Month can't be 13-19
        event.preventDefault();
        return;
      }
    }
  }

  onKeyUp(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const value = target.value || '';
    const digitsOnly = value.replace(/\D/g, '');

    // Auto-insert slashes when typing (not when backspacing/deleting)
    if (event.key !== 'Backspace' && event.key !== 'Delete') {
      if (digitsOnly.length === 2 && value.length === 2) {
        target.value = value + '/';
        target.setSelectionRange(3, 3);
      } else if (digitsOnly.length === 4 && value.length === 5) {
        target.value = value + '/';
        target.setSelectionRange(6, 6);
      }
    }
  }
  
  private handleBackspace(target: HTMLInputElement, currentValue: string, cursorPos: number): void {
    if (cursorPos > 0 && currentValue[cursorPos - 1] === '/') {
      // Delete slash and previous digit
      const newValue = currentValue.substring(0, cursorPos - 2) + currentValue.substring(cursorPos);
      target.value = this.formatDateInput(newValue);
      const newCursorPos = Math.max(0, cursorPos - 2);
      setTimeout(() => {
        target.setSelectionRange(newCursorPos, newCursorPos);
        target.dispatchEvent(new Event('input', { bubbles: true }));
      }, 0);
    } else {
      // Delete one character
      const newValue = currentValue.substring(0, cursorPos - 1) + currentValue.substring(cursorPos);
      const formatted = this.formatDateInput(newValue);
      target.value = formatted;
      const newCursorPos = Math.max(0, cursorPos - 1);
      setTimeout(() => {
        const adjustedPos = formatted.length < currentValue.length 
          ? Math.min(newCursorPos, formatted.length) 
          : newCursorPos;
        target.setSelectionRange(adjustedPos, adjustedPos);
        target.dispatchEvent(new Event('input', { bubbles: true }));
      }, 0);
    }
  }
  
  private handleDelete(target: HTMLInputElement, currentValue: string, cursorPos: number): void {
    if (currentValue[cursorPos] === '/') {
      // Delete slash and next digit
      const newValue = currentValue.substring(0, cursorPos) + currentValue.substring(cursorPos + 2);
      target.value = this.formatDateInput(newValue);
      setTimeout(() => {
        target.setSelectionRange(cursorPos, cursorPos);
        target.dispatchEvent(new Event('input', { bubbles: true }));
      }, 0);
    } else {
      // Delete one character
      const newValue = currentValue.substring(0, cursorPos) + currentValue.substring(cursorPos + 1);
      target.value = this.formatDateInput(newValue);
      setTimeout(() => {
        target.setSelectionRange(cursorPos, cursorPos);
        target.dispatchEvent(new Event('input', { bubbles: true }));
      }, 0);
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const digitsOnly = pastedText.replace(/\D/g, '').substring(0, this.MAX_DIGITS);
    
    if (digitsOnly.length > 0) {
      const target = event.target as HTMLInputElement;
      const formatted = this.formatDateInput(digitsOnly);
      target.value = formatted;
      
      // Trigger input event to process the value
      target.dispatchEvent(new Event('input', { bubbles: true }));
      
      // If fully formatted and valid, select all for easy replacement
      if (formatted.length === this.DATE_FORMAT_LENGTH && this.isValidDateFormat(formatted)) {
        setTimeout(() => target.setSelectionRange(0, formatted.length), 0);
      }
    }
  }

  private formatDateInput(input: string): string {
    const digitsOnly = input.replace(/\D/g, '');
    const limitedDigits = digitsOnly.substring(0, this.MAX_DIGITS);

    if (limitedDigits.length === 0) {
      return '';
    }
    
    if (limitedDigits.length <= 2) {
      return this.formatMonth(limitedDigits);
    }
    
    if (limitedDigits.length <= 4) {
      return this.formatMonthDay(limitedDigits);
    }
    
    return this.formatFullDate(limitedDigits);
  }
  
  private formatMonth(digits: string): string {
    if (digits.length === 2) {
      const month = parseInt(digits, 10);
      if (month < 1 || month > 12) {
        return digits.substring(0, 1);
      }
    }
    return digits;
  }
  
  private formatMonthDay(digits: string): string {
    const month = parseInt(digits.substring(0, 2), 10);
    const day = digits.substring(2);
    
    if (month < 1 || month > 12) {
      return digits.substring(0, 1);
    }
    
    if (day.length === 2) {
      const dayNum = parseInt(day, 10);
      if (dayNum < 1 || dayNum > 31) {
        return `${digits.substring(0, 2)}/${day.substring(0, 1)}`;
      }
    }
    
    return `${digits.substring(0, 2)}/${day}`;
  }
  
  private formatFullDate(digits: string): string {
    const month = parseInt(digits.substring(0, 2), 10);
    const day = parseInt(digits.substring(2, 4), 10);
    const year = digits.substring(4);
    
    if (month < 1 || month > 12) {
      return digits.substring(0, 1);
    }
    
    if (day < 1 || day > 31) {
      return `${digits.substring(0, 2)}/${digits.substring(2, 3)}`;
    }
    
    return `${digits.substring(0, 2)}/${digits.substring(2, 4)}/${year}`;
  }

  private isValidDateFormat(dateString: string): boolean {
    const match = dateString.match(this.DATE_PATTERN);
    if (!match) {
      return false;
    }

    const month = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    if (!this.isValidMonth(month) || !this.isValidDay(day) || !this.isValidYear(year)) {
      return false;
    }

    // Check if date is valid (e.g., not Feb 30)
    const date = new Date(year, month - 1, day);
    return date.getMonth() === month - 1 && 
           date.getDate() === day && 
           date.getFullYear() === year;
  }
  
  private isValidMonth(month: number): boolean {
    return month >= 1 && month <= 12;
  }
  
  private isValidDay(day: number): boolean {
    return day >= 1 && day <= 31;
  }
  
  private isValidYear(year: number): boolean {
    return year >= this.MIN_YEAR && year <= this.MAX_YEAR;
  }

  onNativeDateChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.value = value;
    this.onChange(value);
    this.valueChange.emit(value);
  }

  private parseDateInput(input: string): string {
    const cleaned = input.replace(/\s+/g, '/').replace(/[^\d/]/g, '');
    const parts = cleaned.split('/').filter(p => p);

    if (parts.length < 3) {
      return '';
    }

      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      let year = parts[2];

    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);

    if (!this.isValidMonth(monthNum) || !this.isValidDay(dayNum)) {
      return '';
    }

    // Convert 2-digit year to 4-digit, preserve 4-digit years exactly
      if (year.length === 2) {
        const currentYear = new Date().getFullYear();
        const currentCentury = Math.floor(currentYear / 100) * 100;
      year = String(currentCentury + parseInt(year, 10));
    } else if (year.length !== 4) {
      return '';
    }

        const yearNum = parseInt(year, 10);
    if (!this.isValidYear(yearNum)) {
      return '';
    }

    // Validate date (e.g., not Feb 30)
    const date = new Date(yearNum, monthNum - 1, dayNum);
    if (date.getMonth() !== monthNum - 1 || date.getDate() !== dayNum || date.getFullYear() !== yearNum) {
      return '';
      }

      return `${year}-${month}-${day}`;
    }

  private validateAndParseDate(formatted: string): { date: Date; monthStr: string; dayStr: string; yearStr: string } | null {
    const parts = formatted.split('/');
    if (parts.length !== 3) {
      return null;
    }

    const monthStr = parts[0];
    const dayStr = parts[1];
    const yearStr = parts[2];

    if (monthStr.length !== 2 || dayStr.length !== 2 || yearStr.length !== 4) {
      return null;
    }

    const month = parseInt(monthStr, 10);
    const day = parseInt(dayStr, 10);
    const year = parseInt(yearStr, 10);

    if (!this.isValidMonth(month) || !this.isValidDay(day)) {
      return null;
    }

    const enteredDate = new Date(year, month - 1, day);
    enteredDate.setHours(0, 0, 0, 0);

    if (enteredDate.getMonth() !== month - 1 || 
        enteredDate.getDate() !== day || 
        enteredDate.getFullYear() !== year ||
        isNaN(enteredDate.getTime())) {
      return null;
    }

    return { date: enteredDate, monthStr, dayStr, yearStr };
  }

  onDateBlur(event: FocusEvent): void {
    this.isFocused = false;
    this.isTouched = true;
    this.onTouched();
    this.isTyping = false;
    
    const target = event.target as HTMLInputElement;
    const value = target.value.trim();
    
    if (!value) {
      this.currentInputValue = '';
      this.blur.emit(event);
      return;
    }
    
    if (!this.isValidDateFormat(value)) {
      this.handleInvalidBlurValue(target, value);
      this.blur.emit(event);
      return;
    }
    
    // Valid format - ensure properly formatted and store
    const formatted = this.formatDateInput(value);
    if (formatted !== value) {
      target.value = formatted;
    }
    
    const parts = formatted.split('/');
    if (parts.length === 3) {
      const parsed = `${parts[2]}-${parts[0]}-${parts[1]}`;
      this.currentInputValue = formatted;
      this.value = parsed;
      this.onChange(parsed);
      this.valueChange.emit(parsed);
    }
    
    this.blur.emit(event);
  }
  
  private handleInvalidBlurValue(target: HTMLInputElement, value: string): void {
    if (value.length === this.DATE_FORMAT_LENGTH) {
      // Invalid complete date
      this.clearValue(target);
      return;
    }
    
    // Partial entry - try to format and validate
    const formatted = this.formatDateInput(value);
    if (formatted && this.isValidDateFormat(formatted)) {
      const parts = formatted.split('/');
      if (parts.length === 3) {
        const parsed = `${parts[2]}-${parts[0]}-${parts[1]}`;
        target.value = formatted;
        this.currentInputValue = formatted;
        this.value = parsed;
        this.onChange(parsed);
        this.valueChange.emit(parsed);
        return;
      }
    }
    
    this.clearValue(target);
  }
  
  private clearValue(target: HTMLInputElement): void {
    target.value = this.displayValue || '';
    this.value = '';
    this.currentInputValue = '';
    this.onChange('');
    this.valueChange.emit('');
  }

  onFocus(event: FocusEvent): void {
    this.isFocused = true;
  }

  get hasValue(): boolean {
    return this.value !== null && this.value !== undefined && this.value !== '';
  }

  onDateClick(event: MouseEvent): void {
    this.toggleDropdown();
  }

  get datePickerValue(): string {
    if (!this.value) return '';
    
    if (this.value instanceof Date) {
      const year = this.value.getFullYear();
      const month = String(this.value.getMonth() + 1).padStart(2, '0');
      const day = String(this.value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    if (typeof this.value === 'string') {
      if (this.value.match(this.ISO_DATE_PATTERN)) {
        return this.value;
      }
      const parsed = this.parseDateInput(this.value);
      return parsed.match(this.ISO_DATE_PATTERN) ? parsed : '';
    }
    
    return '';
  }

  get displayValue(): string {
    if (this.isTyping && this.currentInputValue) {
      return this.currentInputValue;
    }
    
    if (!this.value) return '';
    
    if (this.value instanceof Date) {
      return this.formatDate(this.value);
    }
    
    if (typeof this.value === 'string') {
      const isoMatch = this.value.match(this.ISO_DATE_PATTERN);
      if (isoMatch) {
        return `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;
      }
      if (this.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        return this.value;
      }
    }
    
    return this.value;
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  }

  // ControlValueAccessor implementation
  writeValue(value: string | Date): void {
    this.value = value || '';
    // Parent/form updates must not leave "typing" state stuck from a previous edit session.
    this.isTyping = false;
    this.currentInputValue = '';
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get displayLabel(): string {
    return this.config?.label || this.label || '';
  }

  get displayId(): string {
    return this.config?.id || this.id || '';
  }

  get displayName(): string {
    return this.config?.name || this.name || '';
  }

  get displayPlaceholder(): string {
    return this.config?.placeholder || this.placeholder || 'mm/dd/yyyy';
  }

  get displayMin(): string | undefined {
    return this.config?.min || this.min;
  }

  get displayMax(): string | undefined {
    return this.config?.max || this.max;
  }

  get displayFormat(): string {
    return this.config?.format || this.format || 'date';
  }

  get isRequired(): boolean {
    return this.config?.required ?? this.required;
  }

  get isDisabled(): boolean {
    return this.config?.disabled ?? this.disabled;
  }

  get isReadonly(): boolean {
    return this.config?.readonly ?? this.readonly;
  }
}
